import { ConnectionStateEnum } from '@core/model/domain/bankConnection';
import { Result } from '@core/model/framework/Result/Result';
import { GetAccounts } from '@core/usecase/getAccounts';
import { WaitOnWebauthNotification } from '@core/usecase/waitOnWebauthNotification';
import { ProcessEventFactory } from '@shared/models/processEvents';
import { ProcessEventService } from '@shared/services/process-event.service';
import { StoreService } from '@shared/services/store.service';
import { delay, from } from 'rxjs';

export class WaitOnExternalBankLoginVM {
  constructor(
    private processEventService: ProcessEventService,
    private storeService: StoreService,
    private waitOnWebauthNotification: WaitOnWebauthNotification,
    private getAccounts: GetAccounts
  ) {}

  private isPollingStarted = false;

  async onInit() {
    await this.startPollingIfNeeded();
  }

  /*
    The origin page ALWAYS polls the status of its journey: if it survives the
    bank excursion, it learns the outcome the moment the backend reports it —
    success or failure, whatever device the bank came back on. Who then goes
    on with the connection is told by the status itself (destination): this
    page when the journey lands on the widget page (computer, qr code); the
    partner when the relay asked the partner restore (phone, tablet, webview)
    — the reloaded widget completes there, and this page only says so, so
    two widgets never drive the same connection.
    Idempotent: the polling starts at most once per screen.
  */
  async startPollingIfNeeded() {
    if (this.isPollingStarted) {
      return;
    }

    this.isPollingStarted = true;
    await this.waitOnNewBankConnectionNotification();
  }

  async waitOnNewBankConnectionNotification() {
    try {
      const result = await this.waitOnWebauthNotification.execute({
        userToken: this.storeService.getVariableOrThrow('OPEN_BANKING_USER_TOKEN'),
        // Persisted with the journey: this screen can be re-created after the
        // browsing context was reloaded (webview navigated away and came back).
        journeyKey: this.storeService.getVariableOrThrow('WEBAUTH_JOURNEY_KEY'),
      });

      if (result.success === false) {
        // WEBAUTH_JOURNEY_FAILED (bank refusal, completion failed…) and the
        // timeout both end on the retry screen — the failure right away.
        return Result.matchBadWithDefault(result, {
          STOPPED_POLLING_VIA_USER_ACTION: () => {},
          default: () => this.processEventService.push(ProcessEventFactory.SpawnAskToRetryWebauth()),
        });
      }

      const newBankConnection = result.value;

      if (newBankConnection.destination === 'partner') {
        this.processEventService.push(ProcessEventFactory.SpawnWebauthResumedByPartner());
        return;
      }

      if (newBankConnection.state === ConnectionStateEnum.VALID) {
        const userId = this.storeService.getVariableOrThrow('USER_ID');
        const userToken = this.storeService.getVariableOrThrow('OPEN_BANKING_USER_TOKEN');

        /* 
          Delay of 1s between last /connection and /accounts to be sure bank account is
          stored in DB
        */
        from(
          this.getAccounts.execute({
            connectionId: newBankConnection.connectionId,
            userId,
            userToken,
          })
        )
          .pipe(delay(1000))
          .subscribe({
            next: (result) => {
              const accounts = Result.unwrap(result);
              this.processEventService.push(ProcessEventFactory.SpawnAccountsFoundEvent(accounts));
              this.storeService.setVariable('SELECTED_CONNECTION_ID', newBankConnection.connectionId);
            },
            error: (error) => {
              this.processEventService.push(
                ProcessEventFactory.SpawnInternalErrorEvent({
                  errorOrigin: 'WaitOnExternalBankLoginVM - waitOnNewBankConnectionNotification - catch',
                  error,
                })
              );
            },
          });
      } else {
        this.processEventService.push(ProcessEventFactory.SpawnAskToRetryWebauth());
      }
    } catch (error) {
      this.processEventService.push(
        ProcessEventFactory.SpawnInternalErrorEvent({
          errorOrigin: 'WaitOnExternalBankLoginVM - waitOnNewBankConnectionNotification - catch',
          error,
        })
      );
    }
  }

  stopPolling() {
    if (this.isPollingStarted) {
      this.waitOnWebauthNotification.stop();
      this.isPollingStarted = false;
    }
  }

  backToBankChoice() {
    this.processEventService.push(ProcessEventFactory.SpawnGoBackToBankChoiceEvent());
  }

  /*
    Must stay synchronous down to the window.open in the process view model:
    the browser only allows it inside the click handler.
  */
  continueOnThisDevice() {
    this.processEventService.push(ProcessEventFactory.SpawnContinueWebauthOnThisDevice());
  }
}
